// Vendor Imports
import _ from 'lodash';
import { createSelector } from 'reselect';

import getDefaultDomain from 'common/visualizations/helpers/getDefaultDomain';
import {
  SERIES_TYPE_AG_GRID_TABLE,
  SERIES_TYPE_DATA_TABLE,
  SERIES_TYPE_TABLE,
} from 'common/visualizations/views/SvgConstants';

// Constants
import {
  BASEMAP_STYLES,
  CALENDAR,
  COLOR_PALETTE_VALUES,
  DEFAULT_COLOR_PALETTE,
  DEFAULT_SCATTER_CHART_COLUMN_VALUE,
  DEFAULT_SHAPE_FILL_COLOR,
  DEFAULT_SHAPE_FILL_OPACITY,
  DEFAULT_SHAPE_OUTLINE_COLOR,
  DEFAULT_SHAPE_OUTLINE_WIDTH,
  ERROR_BARS_DEFAULT_BAR_COLOR,
  NULL_CHARM_NAME,
  QUANTIFICATION_METHODS,
  RANGE_BUCKET_TYPES,
  SCATTER_CHART,
  SERIES_TYPE_BAR_CHART,
  SERIES_TYPE_COLUMN_CHART,
  SERIES_TYPE_COMBO_CHART_LINE,
  SERIES_TYPE_PIE_CHART,
  SERIES_VARIANT_LINE,
  VECTOR_BASEMAP_STYLES,
  VIF_CONSTANTS,
} from '../constants';

import * as vifSelectors from 'common/visualizations/helpers/VifSelectors';
import { getMapColorGroupNameByColumn } from '../../visualizations/views/map/vifDecorators/commonVifDecorator';
import { type Vif } from 'common/visualizations/vif';
import { type MAP_TYPES } from 'common/visualizations/views/mapConstants';
import { type AuthoringWorkflowState, type VifAuthoring } from '../reducers/types';
import { type ViewColumn } from 'common/types/viewColumn';

export const getAuthoring = (state: VifAuthoring) => state.authoring ?? ({} as VifAuthoring['authoring']);
export const getCheckpointVifs = (state: VifAuthoring) =>
  getAuthoring(state).checkpointVifs ?? ({} as Record<string, Vif>);
export const getCustomColorPaletteError = (state: VifAuthoring) =>
  getAuthoring(state).customColorPaletteError ?? null;
export const getFilters = (state: VifAuthoring) => _.get(state, 'filters', []);
export const getSelectedVisualizationType = (state: VifAuthoring) =>
  getAuthoring(state).selectedVisualizationType;
export const getShowCenteringAndZoomingSaveMessage = (state: VifAuthoring) =>
  getAuthoring(state).showCenteringAndZoomingSaveMessage ?? null;
export const isUserCurrentlyActive = (state: VifAuthoring) =>
  getAuthoring(state).userCurrentlyActive ?? false;
export const getVifs = (state: VifAuthoring) => state.vifs ?? ({} as Record<string, Vif>);
export const getDataTable = (state: AuthoringWorkflowState) => state.vifAuthoring?.vifs?.dataTable;

export const getCurrentVif = createSelector(
  getVifs,
  getSelectedVisualizationType,
  (vifs, selectedVisualizationType): Vif => vifs[selectedVisualizationType ?? ''] ?? ({} as Vif),
);

export const getEmptyVif = createSelector(getCurrentVif, (vif) => {
  const emptyVif: Partial<Vif> = {
    format: {
      type: 'visualization_interchange_format',
      version: VIF_CONSTANTS.LATEST_VERSION,
    },
  };

  const domain = vif.series?.[0]?.dataSource?.domain;
  const datasetUid = vif.series?.[0]?.dataSource?.datasetUid;

  if (domain != null) {
    _.set(emptyVif, 'series[0].dataSource.domain', domain);
  }

  if (datasetUid != null) {
    _.set(emptyVif, 'series[0].dataSource.datasetUid', datasetUid);
  }

  return emptyVif;
});

export const getCurrentSeriesIndex = createSelector(
  getCurrentVif,
  (vif) => vif.currentMapLayerIndex ?? VIF_CONSTANTS.CURRENT_MAP_LAYER_INDEX,
);

export const getConfiguration = createSelector(getCurrentVif, (vif) => vif.configuration);

export const getDimension = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.dataSource?.dimension ?? VIF_CONSTANTS.DEFAULT_DIMENSION,
);

export const getCurrentDrilldownColumnName = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.dataSource?.dimension?.currentDrilldownColumnName,
);

export const getChartFilters = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.dataSource?.filters ?? [],
);

export const getDrilldowns = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.dataSource?.dimension?.drilldowns ?? [],
);

export const getTableColumns = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.dataSource?.dimension?.columns ?? [],
);

export const getTableColumn = (vifAuthoring: VifAuthoring, fieldName: string): ViewColumn | undefined =>
  _.find(getTableColumns(vifAuthoring) as ViewColumn[], { fieldName });

export const getTableHierarchies = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.dataSource?.hierarchies ?? [],
);

export const getActiveHierarchyId = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.dataSource?.activeHierarchyId ?? null,
);

export const getHeaderFormat = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.formatting?.headerFormat ?? {},
);

// Called in common/authoring_workflow/components/panes/PresentationPane/AgGridUpdated/AgGridColumnFormatting/index.tsx#L26
export const getColumnFormats = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.formatting?.columnFormat ?? {},
);

export const getDimensionGrouping = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.dataSource?.dimension?.grouping ?? VIF_CONSTANTS.DEFAULT_DIMENSION_GROUPING,
);

// Warning: This should only be used if you know what you're doing.
// In other words, this is going to randomly choose a VIF and grab its
// dimension.
export const getAnyDimension = createSelector(
  getCurrentVif,
  getVifs,
  getSelectedVisualizationType,
  getCurrentSeriesIndex,
  (vif, vifs, selectedVisualizationType, seriesIndex) => {
    if (_.isEmpty(vif)) {
      if (selectedVisualizationType === 'map') {
        return vifs.map?.series?.[seriesIndex]?.dataSource?.dimension ?? VIF_CONSTANTS.DEFAULT_DIMENSION;
      } else {
        return vifs.columnChart?.series?.[0]?.dataSource?.dimension ?? VIF_CONSTANTS.DEFAULT_DIMENSION;
      }
    }

    return vif.series?.[seriesIndex]?.dataSource?.dimension ?? VIF_CONSTANTS.DEFAULT_DIMENSION;
  },
);

export const getCurrentDimensionColumnName = createSelector(
  getAnyDimension,
  getDrilldowns,
  getCurrentDrilldownColumnName,
  (dimension, drilldowns, currentDrilldownColumnName) => {
    return !_.isEmpty(drilldowns) ? { columnName: currentDrilldownColumnName } : dimension;
  },
);

export const getMeasure = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.dataSource?.measure ?? VIF_CONSTANTS.DEFAULT_MEASURE,
);

export const getReferenceLines = createSelector(getCurrentVif, (vif) => vif.referenceLines ?? []);

export const hasReferenceLineLabels = createSelector(
  getReferenceLines,
  (lines) => lines.filter((line) => !_.isEmpty(line.label)).length > 0,
);

export const getSeriesLength = createSelector(getCurrentVif, (vif) => (vif.series ?? []).length);

export const getSeries = createSelector(getCurrentVif, (vif) => vif.series ?? []);

export const getSeriesZero = createSelector(getCurrentVif, (vif) => vif.series?.[0] ?? []);

export const getNonFlyoutSeries = createSelector(getCurrentVif, vifSelectors.getNonFlyoutSeries);

export const hasSeriesVariantLine = createSelector(
  getSeries,
  (series) => series.find((item) => item.type?.includes(SERIES_VARIANT_LINE)) != null,
);

export const getPrimaryMapLayerSeries = createSelector(getSeries, getSeriesZero, (series, seriesZero) => {
  const primarySeries = series.filter((item) => Boolean(item.primary));

  return _.isEmpty(primarySeries) ? seriesZero : primarySeries[0];
});

export const getPrimaryMapLayerSeriesIndex = createSelector(getSeries, (series) =>
  series.findIndex((item) => Boolean(item.primary)),
);

export const getPointMapSeries = createSelector(getSeries, (series) =>
  series.filter((item) => item.mapOptions?.mapType === 'pointMap'),
);

export const getUseSecondaryAxisForColumns = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.useSecondaryAxisForColumns ?? VIF_CONSTANTS.DEFAULT_SECONDARY_AXIS_FOR_COLUMNS,
);

export const getUseSecondaryAxisForLines = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.useSecondaryAxisForLines ?? VIF_CONSTANTS.DEFAULT_SECONDARY_AXIS_FOR_LINES,
);

export const isUsingPrimaryAxis = createSelector(
  getCurrentVif,
  (vif) =>
    !(vif.configuration?.useSecondaryAxisForColumns ?? VIF_CONSTANTS.DEFAULT_SECONDARY_AXIS_FOR_COLUMNS) ||
    !(vif.configuration?.useSecondaryAxisForLines ?? VIF_CONSTANTS.DEFAULT_SECONDARY_AXIS_FOR_LINES),
);

export const isUsingPrimaryAxisOnly = createSelector(
  getCurrentVif,
  (vif) =>
    !(vif.configuration?.useSecondaryAxisForColumns ?? VIF_CONSTANTS.DEFAULT_SECONDARY_AXIS_FOR_COLUMNS) &&
    !(vif.configuration?.useSecondaryAxisForLines ?? VIF_CONSTANTS.DEFAULT_SECONDARY_AXIS_FOR_LINES),
);

export const isUsingSecondaryAxis = createSelector(
  getCurrentVif,
  (vif) =>
    (vif.configuration?.useSecondaryAxisForColumns ?? VIF_CONSTANTS.DEFAULT_SECONDARY_AXIS_FOR_COLUMNS) ||
    (vif.configuration?.useSecondaryAxisForLines ?? VIF_CONSTANTS.DEFAULT_SECONDARY_AXIS_FOR_LINES),
);

export const isUsingSecondaryAxisOnly = createSelector(
  getCurrentVif,
  (vif) =>
    (vif.configuration?.useSecondaryAxisForColumns ?? VIF_CONSTANTS.DEFAULT_SECONDARY_AXIS_FOR_COLUMNS) &&
    (vif.configuration?.useSecondaryAxisForLines ?? VIF_CONSTANTS.DEFAULT_SECONDARY_AXIS_FOR_LINES),
);

export const getTitle = createSelector(getCurrentVif, (vif) => vif.title ?? VIF_CONSTANTS.DEFAULT_TITLE);

export const getCenterAndZoom = createSelector(getCurrentVif, (vif) => vif.configuration?.mapCenterAndZoom);

export const getDescription = createSelector(
  getCurrentVif,
  (vif) => vif.description ?? VIF_CONSTANTS.DEFAULT_DESCRIPTION,
);

export const getPrimaryColor = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.color?.primary ?? VIF_CONSTANTS.DEFAULT_PRIMARY_COLOR_VALUE,
);

export const getSecondaryColor = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.color?.secondary ?? VIF_CONSTANTS.DEFAULT_SECONDARY_COLOR_VALUE,
);

export const getPointOpacity = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.pointOpacity ?? VIF_CONSTANTS.POINT_OPACITY.DEFAULT,
);

export const getPointSize = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.pointSize ?? VIF_CONSTANTS.POINT_MAP_POINT_SIZE.DEFAULT,
);

export const getMapType = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.mapType ?? VIF_CONSTANTS.DEFAULT_MAP_TYPE_VALUE,
);

export const getTriggerAutoSelectGeoLocationColumn = createSelector(
  getAuthoring,
  (authoring) => authoring.triggerAutoSelectGeoLocationColumn ?? VIF_CONSTANTS.DEFAULT_GEO_LOCATION_COLUMN,
);

export const getCurrentMapLayerEditView = createSelector(
  getAuthoring,
  (authoring) => authoring.currentMapLayerIndexView ?? 'options_view',
);

export const getShowDatasetSelectionModal = createSelector(
  getAuthoring,
  (authoring) => authoring.showDatasetSelectionModal ?? VIF_CONSTANTS.DEFAULT_SHOW_DATASET_SELECTION_VALUE,
);

export const getResizePointsByColumn = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.resizePointsBy ??
    VIF_CONSTANTS.DEFAULT_RESIZE_POINTS_BY_COLUMN_VALUE,
);

export const getColorByColumn = createSelector(
  getCurrentVif,
  (vif) =>
    vif.series?.[SCATTER_CHART.COLOR_BY_SERIES_INDEX]?.dataSource?.measure?.columnName ??
    VIF_CONSTANTS.DEFAULT_COLOR_BY_COLUMN_VALUE,
);

export const getResizeByColumn = createSelector(
  getCurrentVif,
  (vif) =>
    vif.series?.[SCATTER_CHART.RESIZE_BY_SERIES_INDEX]?.dataSource?.measure?.columnName ??
    VIF_CONSTANTS.DEFAULT_RESIZE_BY_COLUMN_VALUE,
);

export const getXAxisColumn = createSelector(
  getCurrentVif,
  (vif) =>
    vif.series?.[SCATTER_CHART.X_AXIS_SERIES_INDEX]?.dataSource?.measure?.columnName ??
    DEFAULT_SCATTER_CHART_COLUMN_VALUE,
);

export const getYAxisColumn = createSelector(
  getCurrentVif,
  (vif) =>
    vif.series?.[SCATTER_CHART.Y_AXIS_SERIES_INDEX]?.dataSource?.measure?.columnName ??
    DEFAULT_SCATTER_CHART_COLUMN_VALUE,
);

export const getMinimumPlotSize = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.chartOptions?.minimumPlotSize ?? SCATTER_CHART.MIN_PLOT_SIZE.DEFAULT,
);

export const getMaximumPlotSize = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.chartOptions?.maximumPlotSize ?? SCATTER_CHART.MAX_PLOT_SIZE.DEFAULT,
);

export const getMinimumPointSize = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.minimumPointSize ?? VIF_CONSTANTS.POINT_MAP_MIN_POINT_SIZE.DEFAULT,
);

export const getMaximumPointSize = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.maximumPointSize ?? VIF_CONSTANTS.POINT_MAP_MAX_POINT_SIZE.DEFAULT,
);

export const getNumberOfDataClasses = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.numberOfDataClasses ??
    VIF_CONSTANTS.NUMBER_OF_DATA_CLASSES.DEFAULT,
);

export const getScatterChartNumberOfDataClasses = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.chartOptions?.numberOfDataClasses ??
    VIF_CONSTANTS.NUMBER_OF_DATA_CLASSES.DEFAULT,
);

export const getColorByBucketsCount = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.colorByBucketsCount ??
    VIF_CONSTANTS.COLOR_BY_BUCKETS_COUNT.DEFAULT,
);

export const getMidpoint = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.mapOptions?.midpoint ?? VIF_CONSTANTS.DEFAULT_MID_POINT,
);

export const getMaxClusteringZoomLevel = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.basemapOptions?.maxClusteringZoomLevel ?? VIF_CONSTANTS.CLUSTERING_ZOOM.DEFAULT,
);

export const getPlotSize = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.plotSize ?? SCATTER_CHART.PLOT_SIZE.DEFAULT,
);

export const getDefaultDisplayDate = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.defaultDisplayDate ?? VIF_CONSTANTS.DEFAULT_DISPLAY_DATE,
);

export const getEndDateColumn = createSelector(
  getCurrentVif,
  (vif) =>
    vif.series?.[CALENDAR.END_DATE_SERIES_INDEX]?.dataSource?.dimension?.columnName ??
    VIF_CONSTANTS.DEFAULT_END_DATE_COLUMN_VALUE,
);

export const getEventTitleColumn = createSelector(
  getCurrentVif,
  (vif) =>
    vif.series?.[CALENDAR.EVENT_TITLE_SERIES_INDEX]?.dataSource?.dimension?.columnName ??
    VIF_CONSTANTS.DEFAULT_EVENT_TITLE_COLUMN_VALUE,
);

export const getStartDateColumn = createSelector(
  getCurrentVif,
  (vif) =>
    vif.series?.[CALENDAR.START_DATE_SERIES_INDEX]?.dataSource?.dimension?.columnName ??
    VIF_CONSTANTS.DEFAULT_START_DATE_COLUMN_VALUE,
);

export const getEventBackgroundColor = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.color?.eventBackgroundColor ??
    VIF_CONSTANTS.DEFAULT_CALENDAR_BACK_GROUND_COLOR,
);

export const getEventOutlineColor = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.color?.eventOutlineColor ?? VIF_CONSTANTS.DEFAULT_CALENDAR_EVENT_OUTLINE_COLOR,
);

export const getEventTextColor = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.color?.eventTextColor ?? VIF_CONSTANTS.DEFAULT_CALENDAR_TEXT_COLOR,
);

export const getMapZoomLevel = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.mapZoomLevel ?? VIF_CONSTANTS.DEFAULT_MAP_ZOOM_LEVEL,
);

export const getPointThreshold = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.basemapOptions?.pointThreshold ?? VIF_CONSTANTS.POINT_THRESHOLD.DEFAULT,
);

export const getClusterRadius = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.basemapOptions?.clusterRadius ?? VIF_CONSTANTS.CLUSTER_RADIUS.DEFAULT,
);

export const getMaxClusterSize = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.basemapOptions?.maxClusterSize ?? VIF_CONSTANTS.CLUSTER_SIZE.DEFAULT,
);

export const getStackRadius = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.basemapOptions?.stackRadius ?? VIF_CONSTANTS.STACK_RADIUS.DEFAULT,
);

export const getColorPointsByColumn = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.colorPointsBy ??
    VIF_CONSTANTS.DEFAULT_COLOR_POINTS_BY_COLUMN_VALUE,
);

export const getLineWeight = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.lineWeight ?? VIF_CONSTANTS.LINE_WEIGHT.DEFAULT,
);

export const getWeighLinesByColumn = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.weighLinesBy ?? VIF_CONSTANTS.DEFAULT_WEIGHT_LINES_BY_COLUMN_VALUE,
);

export const getMinimumLineWeight = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.minimumLineWeight ?? VIF_CONSTANTS.LINE_WEIGHT.MIN,
);

export const getMaximumLineWeight = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.maximumLineWeight ?? VIF_CONSTANTS.LINE_WEIGHT.MAX,
);

export const getColorLinesByColumn = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.colorLinesBy ?? VIF_CONSTANTS.DEFAULT_COLOR_LINES_BY_COLUMN_VALUE,
);

export const getSimplificationLevel = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.simplificationLevel ?? VIF_CONSTANTS.SIMPLIFICATION_LEVEL.DEFAULT,
);

export const getCharmName = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.mapOptions?.charmName ?? NULL_CHARM_NAME,
);

export const getBoundaryColorByColumn = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.colorBoundariesBy ??
    VIF_CONSTANTS.DEFAULT_BOUNDARY_COLOR_BY_COLUMN_VALUE,
);

export const getShapeFillColor = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.mapOptions?.shapeFillColor ?? DEFAULT_SHAPE_FILL_COLOR,
);

export const getShapeFillOpacity = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => {
    const shapeFillOpacity = parseInt(String(vif.series?.[seriesIndex]?.mapOptions?.shapeFillOpacity));

    return Number.isNaN(shapeFillOpacity)
      ? DEFAULT_SHAPE_FILL_OPACITY
      : _.clamp(shapeFillOpacity, VIF_CONSTANTS.SHAPE_FILL_OPACITY.MIN, VIF_CONSTANTS.SHAPE_FILL_OPACITY.MAX);
  },
);

export const getLineColorOpacity = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => {
    const lineColorOpacity = parseInt(String(vif.series?.[seriesIndex]?.mapOptions?.lineColorOpacity), 10);

    return Number.isNaN(lineColorOpacity)
      ? VIF_CONSTANTS.LINE_COLOR_OPACITY.DEFAULT
      : _.clamp(lineColorOpacity, VIF_CONSTANTS.LINE_COLOR_OPACITY.MIN, VIF_CONSTANTS.LINE_COLOR_OPACITY.MAX);
  },
);

export const getShapeOutlineColor = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.shapeOutlineColor ?? DEFAULT_SHAPE_OUTLINE_COLOR,
);

export const getShapeOutlineWidth = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => {
    const shapeOutlineWidth = parseFloat(String(vif.series?.[seriesIndex]?.mapOptions?.shapeOutlineWidth));

    return Number.isNaN(shapeOutlineWidth)
      ? DEFAULT_SHAPE_OUTLINE_WIDTH
      : _.clamp(
          shapeOutlineWidth,
          VIF_CONSTANTS.SHAPE_OUTLINE_WIDTH.MIN,
          VIF_CONSTANTS.SHAPE_OUTLINE_WIDTH.MAX,
        );
  },
);

export const getColorByQuantificationMethod = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.colorByQuantificationMethod ??
    QUANTIFICATION_METHODS.category.value,
);

export const getRangeBucketType = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.rangeBucketType ?? RANGE_BUCKET_TYPES.jenks.value,
);

export const getPointAggregation = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.pointAggregation ?? VIF_CONSTANTS.DEFAULT_POINT_AGGREGATION,
);

export const getBasemapStyle = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.basemapOptions?.basemapStyle ?? VECTOR_BASEMAP_STYLES.basic.value,
);

export const getBasemapType = createSelector(
  getCurrentVif,
  getBasemapStyle,
  (vif, basemapStyle) => BASEMAP_STYLES.find((style) => style.value === basemapStyle)?.title,
);

export const getBasemapOpacity = createSelector(getCurrentVif, (vif) => {
  const basemapOpacity = _.round(vif.configuration?.basemapOptions?.basemapOpacity ?? NaN, 2);

  return Number.isNaN(basemapOpacity)
    ? VIF_CONSTANTS.BASEMAP_OPACITY.MAX
    : _.clamp(basemapOpacity, VIF_CONSTANTS.BASEMAP_OPACITY.MIN, VIF_CONSTANTS.BASEMAP_OPACITY.MAX);
});

export const getNavigationControl = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.basemapOptions?.navigationControl ?? VIF_CONSTANTS.DEFAULT_NAVIGATION_CONTROL,
);

export const getLayerToggleControl = createSelector(
  getCurrentVif,
  (vif) =>
    vif.configuration?.basemapOptions?.layerToggleControl ?? VIF_CONSTANTS.DEFAULT_LAYER_TOGGLE_CONTROL,
);

export const getCastNullAsFalseInSeries = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.castNullAsFalseInSeries ??
    VIF_CONSTANTS.DEFAULT_CAST_NULL_AS_FALSE,
);

export const getGeoCoderControl = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.basemapOptions?.geoCoderControl ?? VIF_CONSTANTS.DEFAULT_GEO_CODER_CONTROL,
);

export const getShowDataTableControl = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.showDataTableControl ?? VIF_CONSTANTS.DEFAULT_SHOW_DATA_TABLE_CONTROL,
);

export const getLockCalendarViewControl = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.lockCalendarViewControl ?? VIF_CONSTANTS.DEFAULT_LOCK_CALENDAR_VIEW_CONTROL,
);

export const getGeoLocateControl = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.basemapOptions?.geoLocateControl ?? VIF_CONSTANTS.DEFAULT_GEO_LOCATION_CONTROL,
);

export const getMapFlyoutTitleColumnName = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.mapOptions?.mapFlyoutTitleColumnName ??
    VIF_CONSTANTS.DEFAULT_MAP_FLYOUT_TITLE_VALUE,
);

export const getAdditionalFlyoutColumns = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.mapOptions?.additionalFlyoutColumns ?? [],
);

export const getRegionMapFlyoutColumnAndAggregations = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.mapOptions?.regionMapFlyoutColumnAndAggregations ?? [],
);

export const getSearchBoundaryUpperLeftLatitude = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.basemapOptions?.searchBoundaryUpperLeftLatitude ?? '',
);

export const getSearchBoundaryUpperLeftLongitude = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.basemapOptions?.searchBoundaryUpperLeftLongitude ?? '',
);

export const getSearchBoundaryLowerRightLatitude = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.basemapOptions?.searchBoundaryLowerRightLatitude ?? '',
);

export const getSearchBoundaryLowerRightLongitude = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.basemapOptions?.searchBoundaryLowerRightLongitude ?? '',
);

export const getColorScale = createSelector(getCurrentVif, (vif) => {
  return {
    negativeColor: vif.configuration?.legend?.negativeColor,
    zeroColor: vif.configuration?.legend?.zeroColor,
    positiveColor: vif.configuration?.legend?.positiveColor,
  };
});

export const getColorPalette = (defaultPalette: string | null = null) =>
  createSelector(
    getCurrentVif,
    getCurrentSeriesIndex,
    (vif, seriesIndex) => vif.series?.[seriesIndex]?.color?.palette ?? defaultPalette,
  );

export const getMapColorPalette = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.color?.palette ?? DEFAULT_COLOR_PALETTE,
);

export const getBaseLayer = createSelector(getCurrentVif, (vif) => vif.configuration?.baseLayerUrl);

export const getBaseLayerOpacity = createSelector(
  getCurrentVif,
  (vif) => (vif.configuration?.baseLayerOpacity ?? 1) * 100,
);

export const getShapefileUid = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.shapefile?.uid ?? VIF_CONSTANTS.DEFALUT_SHAPE_FILE_UID_VALUE,
);

export const getRegionCodingShapefileIdInProcess = createSelector(
  getAuthoring,
  (authoring) =>
    authoring.regionCodingShapefileIdInProcess ?? VIF_CONSTANTS.DEFAULT_REGION_CODING_SHAPE_FILE_ID_VALUE,
);

export const getUnitOne = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.unit?.one ?? VIF_CONSTANTS.DEFAULT_FLYOUT_UNIT,
);

export const getUnitOther = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.unit?.other ?? VIF_CONSTANTS.DEFAULT_FLYOUT_UNIT,
);

export const getRowInspectorTitleColumnName = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.rowInspectorTitleColumnName ?? VIF_CONSTANTS.DEFAULT_ROW_INSPECTOR_TITLE_VALUE,
);

export const getDatasetUid = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.dataSource?.datasetUid ?? VIF_CONSTANTS.DEFAULT_DATASET_UID,
);

export const getLegendPosition = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.legendPosition ?? VIF_CONSTANTS.DEFAULT_LEGEND_POSITION,
);

export const getMapLayerName = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.dataSource?.name ?? VIF_CONSTANTS.DEFAULT_MAP_LAYER_NAME,
);

export const getMapLayerSource = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.dataSource?.source ?? VIF_CONSTANTS.DEFAULT_MAP_LAYER_SOURCE,
);

export const getDomain = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.dataSource?.domain || getDefaultDomain(),
);

export const getAxisLabels = createSelector(getCurrentVif, (vif) => vif.configuration?.axisLabels);

export const getViewSourceDataLink = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.viewSourceDataLink ?? VIF_CONSTANTS.DEFAULT_VIEW_SOURCE_DATA_LINK,
);

export const getShowTableActions = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.showProcessDiscoveryAction ?? VIF_CONSTANTS.DEFAULT_SHOW_TABLE_ACTIONS,
);

export const getShowExistingActionColumn = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.showExistingActionColumn ?? VIF_CONSTANTS.DEFAULT_SHOW_EXISTING_ACTION_COLUMN,
);

export const getSelectedExistingColumn = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.selectedExistingColumn ?? VIF_CONSTANTS.DEFAULT_SELECTED_EXISING_COLUMN,
);

export const getSelectedActionForExistingColumn = createSelector(
  getCurrentVif,
  (vif) =>
    vif.configuration?.selectedActionForExistingColumn ??
    VIF_CONSTANTS.DEFAULT_SELECTED_ACTION_FOR_EXISING_COLUMN,
);

export const getNewTableActions = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.newTableActions ?? VIF_CONSTANTS.DEFAULT_NEW_TABLE_ACTIONS,
);

export const getActionsPrimaryKeyColumn = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.actionsPrimaryKeyColumn ?? '',
);

export const getActionsMediaType = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.actionsMediaType ?? '',
);

export const getContextFieldMappings = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.contextFieldMappings ?? [],
);

export const getShowDimensionLabels = createSelector(getCurrentVif, vifSelectors.getShowDimensionLabels);

export const getDimensionLabelRotationAngle = createSelector(
  getCurrentVif,
  vifSelectors.getDimensionLabelRotationAngle,
);

export const getLineStylePoints = createSelector(getCurrentVif, vifSelectors.getLineStylePoints);

export const getAnnotations = createSelector(getCurrentVif, (vif) => vif.series?.[0]?.annotations ?? []);

export const getShowLineValueLabels = createSelector(getCurrentVif, vifSelectors.getShowLineValueLabels);

export const getShowNullsAsFalse = createSelector(getCurrentVif, vifSelectors.getShowNullsAsFalse);

export const getShowValueLabels = createSelector(getCurrentVif, vifSelectors.getShowValueLabels);

export const getShowValueLabelsAsPercent = createSelector(
  getCurrentVif,
  vifSelectors.getShowValueLabelsAsPercent,
);

export const getShowLegend = (defaultValue = VIF_CONSTANTS.DEFAULT_SHOW_LEGEND) =>
  createSelector(
    getCurrentVif,
    getCurrentSeriesIndex,
    (vif, seriesIndex) => vif.series?.[seriesIndex]?.showLegend ?? defaultValue,
  );

export const getShowLegendForMap = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.showLegendForMap ?? VIF_CONSTANTS.DEFAULT_SHOW_LEGEND_FOR_MAP,
);

export const getShowAnnotationsInLegend = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.showAnnotationsInLegend ?? VIF_CONSTANTS.DEFAULT_SHOW_ANNOTATIONS_IN_LEGEND,
);

export const getShowLegendOpened = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.showLegendOpened ?? VIF_CONSTANTS.DEFAULT_SHOW_LEGEND_OPENED,
);

export const getShowMultiplePointsSymbolInLegend = createSelector(
  getCurrentVif,
  (vif) =>
    vif.configuration?.showMultiplePointsSymbolInLegend ??
    VIF_CONSTANTS.DEFAULT_SHOW_MULTIPLE_POINTS_SYMBOL_IN_LEGEND,
);

export const getShowSlicePercentsInFlyouts = createSelector(
  getCurrentVif,
  vifSelectors.getShowSlicePercentsInFlyouts,
);

export const getWrapDimensionLabels = createSelector(getCurrentVif, vifSelectors.getWrapDimensionLabels);

export const getHideDuplicateGroups = createSelector(getCurrentVif, vifSelectors.getHideDuplicateGroups);

export const getIsIndented = createSelector(getCurrentVif, vifSelectors.getIsIndented);

export const getXAxisScalingMode = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.xAxisScalingMode,
);

export const getMeasureAxisMaxValue = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.measureAxisMaxValue ?? VIF_CONSTANTS.DEFAULT_MEASURE_AXIS_MAX_VALUE,
);

export const getMeasureAxisMinValue = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.measureAxisMinValue ?? VIF_CONSTANTS.DEFAULT_MEASURE_AXIS_MIN_VALUE,
);

export const getMeasureAxisPrecision = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.measureAxisPrecision ?? VIF_CONSTANTS.DEFAULT_MEASURE_AXIS_PRECISION,
);

export const getMapLegendPrecision = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.mapLegendPrecision ?? VIF_CONSTANTS.DEFAULT_MAP_LEGEND_PRECISION,
);

export const getMapFlyoutPrecision = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.mapFlyoutPrecision ?? VIF_CONSTANTS.DEFAULT_MAP_FLYOUT_PRECISION,
);

export const getSecondaryMeasureAxisPrecision = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.secondaryMeasureAxisPrecision ?? VIF_CONSTANTS.DEFAULT_MEASURE_AXIS_PRECISION,
);

export const getMeasureAxisScale = createSelector(getCurrentVif, vifSelectors.getMeasureAxisScale);

export const isLogarithmicScale = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.logarithmicScale ?? VIF_CONSTANTS.DEFAULT_LOGARITHMIC_SCALE,
);

export const getSecondaryMeasureAxisMaxValue = createSelector(
  getCurrentVif,
  (vif) =>
    vif.configuration?.secondaryMeasureAxisMaxValue ?? VIF_CONSTANTS.DEFAULT_SECONDARY_MEASURE_AXIS_MAX_VALUE,
);

export const getSecondaryMeasureAxisMinValue = createSelector(
  getCurrentVif,
  (vif) =>
    vif.configuration?.secondaryMeasureAxisMinValue ?? VIF_CONSTANTS.DEFAULT_SECONDARY_MEASURE_AXIS_MIN_VALUE,
);

export const getSecondaryMeasureAxisScale = createSelector(
  getCurrentVif,
  vifSelectors.getSecondaryMeasureAxisScale,
);

export const getComboChartLineSeries = createSelector(getSeries, (series) =>
  series.filter((item) => item.type === SERIES_TYPE_COMBO_CHART_LINE),
);

export const hasComboChartLineSeries = createSelector(getComboChartLineSeries, (series) => series.length > 0);

export const isUsingSecondaryAxisForLines = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.useSecondaryAxisForLines ?? false,
);

export const getDimensionGroupingColumnName = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) =>
    vif.series?.[seriesIndex]?.dataSource?.dimension?.grouping?.columnName ??
    VIF_CONSTANTS.DEFAULT_DIMENSION_GROUPING_COLUMN_VALUE,
);

export const getErrorBarsLowerBoundColumnName = createSelector(
  getCurrentVif,
  (vif) => vif.series?.[0]?.errorBars?.lowerBoundColumnName,
);

export const getErrorBarsUpperBoundColumnName = createSelector(
  getCurrentVif,
  (vif) => vif.series?.[0]?.errorBars?.upperBoundColumnName,
);

export const getErrorBarsColor = createSelector(
  getCurrentVif,
  (vif) => vif.series?.[0]?.errorBars?.barColor ?? ERROR_BARS_DEFAULT_BAR_COLOR,
);

export const hasErrorBars = createSelector(
  getErrorBarsLowerBoundColumnName,
  getErrorBarsUpperBoundColumnName,
  (lowerBoundColumnName, upperBoundColumnName) =>
    lowerBoundColumnName !== undefined && upperBoundColumnName !== undefined,
);

export const getStacked = createSelector(getCurrentVif, (vif) => vif.series?.[0]?.stacked ?? false);

export const hasDimensionGroupingColumnName = createSelector(
  getDimensionGroupingColumnName,
  (groupingColumnName) => !_.isEmpty(groupingColumnName),
);

export const getColorPaletteGroupingColumnName = createSelector(
  getCurrentVif,
  getSelectedVisualizationType,
  getPointAggregation,
  getDimensionGroupingColumnName,
  getMapType,
  getCurrentSeriesIndex,
  (vif, visualizationType, pointAggregation, grouping, mapType, seriesIndex) => {
    if (visualizationType === 'pieChart') {
      return vif.series?.[0]?.dataSource?.dimension?.columnName ?? VIF_CONSTANTS.DEFAULT_DIMENSION;
    }

    if (visualizationType === 'scatterChart') {
      return vif.series?.[SCATTER_CHART.COLOR_BY_SERIES_INDEX]?.dataSource?.measure?.columnName;
    }

    if (visualizationType === 'map') {
      // getMapColorGroupNameByColumn is typed as a DecoratedVif method (via `this`), but every argument
      // it would otherwise default from `this` is supplied explicitly here, so `this` is never read.
      const getMapColorGroupNameByColumnPlain = getMapColorGroupNameByColumn as unknown as (
        vif: Vif,
        mapType: MAP_TYPES | null | undefined,
        pointAggregation: 'heat_map' | 'region_map' | 'none' | undefined,
        seriesIndex: number,
      ) => string | null;

      return getMapColorGroupNameByColumnPlain(vif, mapType, pointAggregation, seriesIndex);
    }

    return grouping;
  },
);

// getCustomColorPalettes and getCustomColorPaletteForNewGLMaps stay on _.get rather than direct
// property access: several consumers treat series.color.customPalette as a flat ColorPalette map,
// while its canonical type (CustomColorPaletteWithQuantification) is nested by quantification method.
// Direct access would just relocate that pre-existing mismatch onto this file instead of fixing it.
export const getCustomColorPalettes = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => _.get(vif, ['series', seriesIndex, 'color', 'customPalette'], {}),
);

export const getCustomColorPaletteForNewGLMaps = createSelector(
  getCurrentVif,
  getColorByBucketsCount,
  getCurrentSeriesIndex,
  (vif, colorByBucketsCount, seriesIndex) =>
    _.get(
      vif,
      ['series', seriesIndex, 'color', 'customPalette'],
      COLOR_PALETTE_VALUES[DEFAULT_COLOR_PALETTE].slice(0, colorByBucketsCount + 1),
    ),
);

export const hasCustomColorPalette = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.color?.palette === 'custom',
);

export const getDateDisplayFormat = createSelector(
  getCurrentVif,
  (vif) => vif.series?.[0]?.dataSource?.dateDisplayFormat,
);

export const getGroupingOrderBy = createSelector(
  getCurrentVif,
  (vif) => vif.series?.[0]?.dataSource?.dimension?.grouping?.orderBy,
);

export const getOrderBy = createSelector(getCurrentVif, (vif) => vif.series?.[0]?.dataSource?.orderBy);

export const getPrecision = createSelector(getCurrentVif, (vif) => vif.series?.[0]?.dataSource?.precision);

export const getTreatNullValuesAsZero = createSelector(
  getCurrentVif,
  (vif) => vif.configuration?.treatNullValuesAsZero,
);

export const getLimitCount = createSelector(
  getCurrentVif,
  (vif) => vif.series?.[0]?.dataSource?.limit ?? VIF_CONSTANTS.DEFAULT_LIMIT_COUNT,
);

export const getShowOtherCategory = createSelector(getCurrentVif, (vif) => {
  return vif.series?.[0]?.dataSource?.limit !== null && vif.configuration?.showOtherCategory;
});

export const getVisualizationType = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => {
    const type = vif.series?.[seriesIndex]?.type ?? VIF_CONSTANTS.DEFAULT_VISUALIZATION_TYPE;

    return type === null ? VIF_CONSTANTS.DEFAULT_VISUALIZATION_TYPE : type.split('.')[0];
  },
);

export const hasVisualizationType = createSelector(getVisualizationType, (type) => typeof type === 'string');

export const hasVisualizationDimension = createSelector(getDimension, (dimension) => {
  const columnName = dimension?.columnName ?? null;
  return columnName !== null;
});

export const hasDimensionWithDrilldowns = createSelector(
  getVisualizationType,
  getDrilldowns,
  (type, drilldowns) => {
    const visualizationType =
      type === SERIES_TYPE_BAR_CHART || type === SERIES_TYPE_COLUMN_CHART || type === SERIES_TYPE_PIE_CHART;
    return visualizationType && drilldowns.length > 0;
  },
);

export const isGrouping = createSelector(
  getDimensionGroupingColumnName,
  (dimensionGroupingColumnName) => typeof dimensionGroupingColumnName === 'string',
);

export const hasMultipleNonFlyoutSeries = createSelector(getNonFlyoutSeries, (series) => series.length > 1);

export const isGroupingOrHasMultipleNonFlyoutSeries = createSelector(
  getCurrentVif,
  vifSelectors.isGroupingOrHasMultipleNonFlyoutSeries,
);

export const isColorByColumnConfigured = createSelector(
  getColorByColumn,
  (colorByColumn) => colorByColumn != null,
);

export const isStacked = createSelector(getCurrentVif, vifSelectors.isVifStacked);

export const isOneHundredPercentStacked = createSelector(
  getCurrentVif,
  (vif) => vif.series?.[0]?.stacked?.oneHundredPercent ?? false,
);

export const isRegionMap = createSelector(getVisualizationType, (type) => type === 'regionMap');

export const isCurrentMapLayerPrimary = createSelector(
  getCurrentVif,
  getCurrentSeriesIndex,
  (vif, seriesIndex) => vif.series?.[seriesIndex]?.primary ?? VIF_CONSTANTS.DEFAULT_MAP_LAYER_PRIMARY_VALUE,
);

// getSeriesZero stays on _.get here: its default value is `[]` (an array) even though a single
// series is normally an object, so the param type is a Series/never[] union that direct property
// access can't cleanly narrow.
export const isValidRegionMapVif = createSelector(
  getDimension,
  getMeasure,
  getDatasetUid,
  getDomain,
  getSeriesZero,
  (dimension, measure, datasetUid, domain, series) => {
    const hasComputedColumnName = typeof _.get(series, 'computedColumnName') === 'string';
    const hasShapeFileUid = typeof _.get(series, 'shapefile.uid') === 'string';
    const hasShapeFilePrimaryKey = typeof _.get(series, 'shapefile.primaryKey') === 'string';
    const hasDimension = typeof dimension?.columnName === 'string';

    const hasMeasureAggregation = typeof measure?.aggregationFunction === 'string';

    const hasDatasetUid = typeof datasetUid === 'string';
    const hasDomain = typeof domain === 'string';

    return (
      hasComputedColumnName &&
      hasShapeFileUid &&
      hasShapeFilePrimaryKey &&
      hasDimension &&
      hasDatasetUid &&
      hasDomain &&
      hasMeasureAggregation
    );
  },
);

export const isChartOfType = (types: string[]) =>
  createSelector(getVisualizationType, (type) => types.includes(type ?? ''));

export const isBarChart = createSelector(getCurrentVif, vifSelectors.isBarVisualization);

export const isValidBarChartVif = createSelector(
  getDimension,
  getDatasetUid,
  getDomain,
  (dimension, datasetUid, domain) => {
    const hasDimension = typeof dimension?.columnName === 'string';
    const hasDatasetUid = typeof datasetUid === 'string';
    const hasDomain = typeof domain === 'string';

    return hasDimension && hasDatasetUid && hasDomain;
  },
);

export const isColumnChart = createSelector(getVisualizationType, (type) => type === 'columnChart');

export const isValidColumnChartVif = createSelector(
  getDimension,
  getDatasetUid,
  getDomain,
  (dimension, datasetUid, domain) => {
    const hasDimension = typeof dimension?.columnName === 'string';
    const hasDatasetUid = typeof datasetUid === 'string';
    const hasDomain = typeof domain === 'string';

    return hasDimension && hasDatasetUid && hasDomain;
  },
);

export const isComboChart = createSelector(getVisualizationType, (visualizationType) => {
  const parts = (visualizationType || '').split('.');
  const type = parts.length > 0 ? parts[0] : visualizationType;

  return type === 'comboChart';
});

export const isValidComboChartVif = createSelector(
  getDimension,
  getDatasetUid,
  getDomain,
  (dimension, datasetUid, domain) => {
    const hasDimension = typeof dimension?.columnName === 'string';
    const hasDatasetUid = typeof datasetUid === 'string';
    const hasDomain = typeof domain === 'string';

    return hasDimension && hasDatasetUid && hasDomain;
  },
);

export const isScatterChart = createSelector(getCurrentVif, vifSelectors.isScatterVisualization);

export const isValidScatterChartVif = createSelector(getDatasetUid, getDomain, (datasetUid, domain) => {
  const hasDatasetUid = typeof datasetUid === 'string';
  const hasDomain = typeof domain === 'string';

  return hasDatasetUid && hasDomain;
});

export const isCalendar = createSelector(getVisualizationType, (type) => type === 'calendar');

export const isValidCalendarVif = createSelector(getDatasetUid, getDomain, (datasetUid, domain) => {
  const hasDatasetUid = typeof datasetUid === 'string';
  const hasDomain = typeof domain === 'string';

  return hasDatasetUid && hasDomain;
});

// May need to update this to include AGgrid table
export const isTable = createSelector(
  getVisualizationType,
  (type) => type === SERIES_TYPE_TABLE || type === SERIES_TYPE_AG_GRID_TABLE,
);

export const isValidTableVif = createSelector(
  getTableColumns,
  getDatasetUid,
  getDomain,
  (tableColumns, datasetUid, domain) => {
    const hasDatasetUid = typeof datasetUid === 'string';
    const hasDomain = typeof domain === 'string';
    const hasColumns = !_.isEmpty(tableColumns);

    return hasDatasetUid && hasDomain && hasColumns;
  },
);

export const isDataTable = createSelector(getVisualizationType, (type) => type === SERIES_TYPE_DATA_TABLE);

export const isValidDataTableVif = createSelector(getDatasetUid, getDomain, (datasetUid, domain) => {
  const hasDatasetUid = typeof datasetUid === 'string';
  const hasDomain = typeof domain === 'string';

  return hasDatasetUid && hasDomain;
});

export const isAgTable = createSelector(getVisualizationType, (type) => type === SERIES_TYPE_AG_GRID_TABLE);

export const isValidAgTableVif = createSelector(
  getTableColumns,
  getDatasetUid,
  getDomain,
  (tableColumns, datasetUid, domain) => {
    const hasDatasetUid = typeof datasetUid === 'string';
    const hasDomain = typeof domain === 'string';
    const hasColumns = !_.isEmpty(tableColumns);

    return hasDatasetUid && hasDomain && hasColumns;
  },
);

export const hasSelectedTableVisualizationColumns = createSelector(
  getDimension,
  isTable,
  (dimension, isTableType) => {
    const selectedColumnsLength = dimension?.columns?.length ?? 0;
    return selectedColumnsLength > 0 && isTableType;
  },
);

export const isHistogram = createSelector(getVisualizationType, (type) => type === 'histogram');

export const isValidHistogramVif = createSelector(
  getDimension,
  getMeasure,
  getDatasetUid,
  getDomain,
  (dimension, measure, datasetUid, domain) => {
    const hasDimension = typeof dimension?.columnName === 'string';
    const hasDatasetUid = typeof datasetUid === 'string';
    const hasDomain = typeof domain === 'string';

    return hasDimension && hasDatasetUid && hasDomain;
  },
);

export const isFeatureMap = createSelector(getVisualizationType, (type) => type === 'featureMap');

export const isNewGLMap = createSelector(getVisualizationType, (type) => {
  return type === 'map';
});

// getSeriesZero stays on _.get here: its default value is `[]` (an array) even though a single
// series is normally an object, so the param type is a Series/never[] union that direct property
// access can't cleanly narrow.
export const isValidNewGLMapVif = createSelector(getSeriesZero, (series) => {
  const hasDimension = typeof _.get(series, 'dataSource.dimension.columnName') === 'string';
  const hasDatasetUid = typeof _.get(series, 'dataSource.datasetUid') === 'string';
  const hasComputedColumnName = typeof _.get(series, 'computedColumnName') === 'string';
  const hasShapeFileUid = typeof _.get(series, 'shapefile.uid') === 'string';
  const hasMeasureAggregation = typeof _.get(series, 'dataSource.measure.aggregationFunction') === 'string';

  return (
    (hasDimension && hasDatasetUid) ||
    (hasDimension && hasDatasetUid && hasComputedColumnName && hasShapeFileUid && hasMeasureAggregation)
  );
});

export const isValidFeatureMapVif = createSelector(
  getDimension,
  getDatasetUid,
  getDomain,
  (dimension, datasetUid, domain) => {
    const hasDimension = typeof dimension?.columnName === 'string';
    const hasDatasetUid = typeof datasetUid === 'string';
    const hasDomain = typeof domain === 'string';

    return hasDimension && hasDatasetUid && hasDomain;
  },
);

export const isTimelineChart = createSelector(getVisualizationType, (type) => type === 'timelineChart');

export const isValidTimelineChartVif = createSelector(
  getDimension,
  getMeasure,
  getDatasetUid,
  getDomain,
  (dimension, measure, datasetUid, domain) => {
    const hasDimension = typeof dimension?.columnName === 'string';
    const hasDatasetUid = typeof datasetUid === 'string';
    const hasDomain = typeof domain === 'string';

    return hasDimension && hasDatasetUid && hasDomain;
  },
);

export const isPieChart = createSelector(getVisualizationType, (type) => type === 'pieChart');

export const isValidPieChartVif = createSelector(
  getDimension,
  getMeasure,
  getDatasetUid,
  getDomain,
  (dimension, measure, datasetUid, domain) => {
    const hasDimension = typeof dimension?.columnName === 'string';
    const hasDatasetUid = typeof datasetUid === 'string';
    const hasDomain = typeof domain === 'string';

    return hasDimension && hasDatasetUid && hasDomain;
  },
);

// reselect@3's createSelector types only support up to 12 input selectors, so the 14
// "is this chart type" / "is its vif valid" pairs below are grouped into 3 intermediate
// selectors and OR'd together in isValidVif rather than passed to a single createSelector call.
const isValidVifGroup1 = createSelector(
  isBarChart,
  isValidBarChartVif,
  isNewGLMap,
  isValidNewGLMapVif,
  isFeatureMap,
  isValidFeatureMapVif,
  isRegionMap,
  isValidRegionMapVif,
  isColumnChart,
  isValidColumnChartVif,
  isComboChart,
  isValidComboChartVif,
  (
    isBarChart,
    validBarChart,
    isNewGLMap,
    validNewMap,
    isFeatureMap,
    validFeatureMap,
    isRegionMap,
    validRegionMap,
    isColumnChart,
    validColumnChart,
    isComboChart,
    validComboChart,
  ) =>
    (isBarChart && validBarChart) ||
    (isNewGLMap && validNewMap) ||
    (isFeatureMap && validFeatureMap) ||
    (isRegionMap && validRegionMap) ||
    (isColumnChart && validColumnChart) ||
    (isComboChart && validComboChart),
);

const isValidVifGroup2 = createSelector(
  isCalendar,
  isValidCalendarVif,
  isScatterChart,
  isValidScatterChartVif,
  isPieChart,
  isValidPieChartVif,
  isTimelineChart,
  isValidTimelineChartVif,
  isHistogram,
  isValidHistogramVif,
  isTable,
  isValidTableVif,
  (
    isCalendar,
    validCalendar,
    isScatterChart,
    validScatterChart,
    isPieChart,
    validPieChart,
    isTimelineChart,
    validTimelineChart,
    isHistogram,
    validHistogramVif,
    isTable,
    validTableVif,
  ) =>
    (isCalendar && validCalendar) ||
    (isScatterChart && validScatterChart) ||
    (isPieChart && validPieChart) ||
    (isTimelineChart && validTimelineChart) ||
    (isHistogram && validHistogramVif) ||
    (isTable && validTableVif),
);

const isValidVifGroup3 = createSelector(
  isDataTable,
  isValidDataTableVif,
  isAgTable,
  isValidAgTableVif,
  (isDataTable, validDataTable, isAgTable, validAgTable) =>
    (isDataTable && validDataTable) || (isAgTable && validAgTable),
);

export const isValidVif = createSelector(
  isValidVifGroup1,
  isValidVifGroup2,
  isValidVifGroup3,
  (group1, group2, group3) => group1 || group2 || group3,
);

export const isMap = createSelector(
  isNewGLMap,
  isRegionMap,
  isFeatureMap,
  (isNewGLMap, isRegionMap, isFeatureMap) => isNewGLMap || isRegionMap || isFeatureMap,
);

export const isRenderableMap = createSelector(
  isNewGLMap,
  isValidNewGLMapVif,
  isRegionMap,
  isValidRegionMapVif,
  isFeatureMap,
  isValidFeatureMapVif,
  (isNewGLMap, isValidNewGLMapVif, isRegionMap, isValidRegionMapVif, isFeatureMap, isValidFeatureMapVif) => {
    return (
      (isNewGLMap && isValidNewGLMapVif) ||
      (isFeatureMap && isValidFeatureMapVif) ||
      (isRegionMap && isValidRegionMapVif)
    );
  },
);

export const isInsertableVisualization = createSelector(
  getShowCenteringAndZoomingSaveMessage,
  isValidVif,
  (showCenteringAndZoomingSaveMessage, isValidVif) => {
    return !showCenteringAndZoomingSaveMessage && isValidVif;
  },
);

export const hasMadeChangesToVifs = createSelector(
  getVifs,
  getCheckpointVifs,
  (modifiedVifs, checkpointVifs) => {
    const clonedModifiedVifs = _.cloneDeep(modifiedVifs);
    const clonedCheckpointVifs = _.cloneDeep(checkpointVifs);

    // Data tables aren't a configurable visualization
    // and have their own thing going on, so
    // don't compare them.
    _.unset(clonedModifiedVifs, 'dataTable');
    _.unset(clonedCheckpointVifs, 'dataTable');

    return !_.isEqual(clonedModifiedVifs, clonedCheckpointVifs);
  },
);
